/**
 * The worlds and their catalogues, fetched once per visit and shared.
 *
 * `GET /api/universes` lists the worlds; `GET /api/universes/:slug/catalogue`
 * returns one world's whole published catalogue, and each of that world's
 * pages is a view over it. Both are kept for the life of the tab, so moving
 * between pages does not ask again.
 *
 * Also the one place that picks a field by language. Content in the database
 * is bilingual except `note_bg`, which has no English twin: the English view
 * omits it rather than showing Bulgarian.
 */

import { useEffect, useState } from 'react'
import type {
  CatalogueResponse,
  CatalogueSeries,
  CatalogueUniverse,
  CatalogueWork,
  WorldSummary,
  WorldsResponse,
} from '../../shared/catalogue.ts'
import type { ApiResponse } from '../../shared/api.ts'
import type { Locale } from '../../shared/locale.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { browserSeedNow, catalogueIn, useSeededCatalogue, useSeededWorlds, worldsIn } from './seed.ts'

export type Loaded = Extract<CatalogueResponse, { status: 'loaded' }>

export type WorldState =
  | { status: 'loading' }
  | { status: 'error' }
  /** No such world, or not published: the Worker answered this page with a 404. */
  | { status: 'not-found' }
  | CatalogueResponse

export type WorldsState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; worlds: WorldSummary[] }

/** 404 is an answer here, not a failure: it means "there is no such world". */
async function getOrNull<T>(path: string): Promise<T | null> {
  const response = await fetch(path, { credentials: 'same-origin' })
  if (response.status === 404) return null
  const body = (await response.json()) as ApiResponse<T>
  if (!body.ok) throw new Error(body.error.key)
  return body.data
}

const catalogues = new Map<string, Promise<CatalogueResponse | null>>()
let worldsRequest: Promise<WorldsResponse | null> | undefined

function remembered<T>(start: () => Promise<T>, forget: () => void): Promise<T> {
  return start().catch((cause: unknown) => {
    // A failed fetch is not remembered: the next page tries again.
    forget()
    throw cause
  })
}

function fetchCatalogue(world: string): Promise<CatalogueResponse | null> {
  // Rendered by the server with this data: no request needed (lib/seed.ts).
  // In the browser only (this runs in effects): the seed the page loaded with.
  const seeded = catalogueIn(browserSeedNow(), world)
  if (seeded !== null) return Promise.resolve(seeded)
  let pending = catalogues.get(world)
  if (pending === undefined) {
    pending = remembered(
      () => getOrNull<CatalogueResponse>(`/api/universes/${encodeURIComponent(world)}/catalogue`),
      () => catalogues.delete(world),
    )
    catalogues.set(world, pending)
  }
  return pending
}

function fetchWorlds(): Promise<WorldsResponse | null> {
  const seeded = worldsIn(browserSeedNow())
  if (seeded !== null) return Promise.resolve(seeded)
  worldsRequest ??= remembered(
    () => getOrNull<WorldsResponse>('/api/universes'),
    () => {
      worldsRequest = undefined
    },
  )
  return worldsRequest
}

export function useWorldCatalogue(world: string): WorldState {
  // Seeded when the server rendered this page: the first render, on the
  // server and then in the browser, is the loaded page, not "loading".
  const seededCatalogue = useSeededCatalogue(world)
  const [state, setState] = useState<WorldState>(() => seededCatalogue ?? { status: 'loading' })
  useEffect(() => {
    // No reset to 'loading' here: the provider is keyed by the world, so a
    // different world is a fresh component with a fresh state.
    let live = true
    fetchCatalogue(world).then(
      (data) => {
        if (live) setState(data ?? { status: 'not-found' })
      },
      () => {
        if (live) setState({ status: 'error' })
      },
    )
    return () => {
      live = false
    }
  }, [world])
  return state
}

export function useWorlds(): WorldsState {
  const seeded = useSeededWorlds()
  const [state, setState] = useState<WorldsState>(() =>
    seeded === null ? { status: 'loading' } : { status: 'ready', worlds: seeded.worlds },
  )
  useEffect(() => {
    let live = true
    fetchWorlds().then(
      (data) => {
        if (live) setState({ status: 'ready', worlds: data?.worlds ?? [] })
      },
      () => {
        if (live) setState({ status: 'error' })
      },
    )
    return () => {
      live = false
    }
  }, [])
  return state
}

// ---------------------------------------------------------------------------
// Picking the language

type WorldText = 'tagline' | 'description' | 'intro'

/** `pick(world, 'tagline', locale)` reads `tagline_bg` or `tagline_en`. */
export function pick(
  row: Partial<Record<`${WorldText}_${Locale}`, string | null>>,
  field: WorldText,
  locale: Locale,
): string | null {
  return row[`${field}_${locale}`] ?? null
}

export function worldName(world: CatalogueUniverse | WorldSummary, locale: Locale): string {
  return locale === 'bg' ? world.name_bg : world.name_en
}

/** The title to lead with; the other one is shown only on the book's own page. */
export function mainTitle(work: CatalogueWork, locale: Locale): string {
  return locale === 'bg' ? (work.title_bg ?? work.title_en) : work.title_en
}

/** The title in the other language, or null when there is none to show. */
export function otherTitle(work: CatalogueWork, locale: Locale): string | null {
  return locale === 'bg' ? (work.title_bg === null ? null : work.title_en) : work.title_bg
}

export function synopsis(work: CatalogueWork, locale: Locale): string | null {
  return locale === 'bg' ? work.synopsis_bg : work.synopsis_en
}

export function seriesName(series: CatalogueSeries, locale: Locale): string {
  return locale === 'bg' ? series.name_bg : series.name_en
}

export function seriesDescription(series: CatalogueSeries, locale: Locale): string | null {
  return locale === 'bg' ? series.description_bg : series.description_en
}

/** Bulgarian only: there is no English note, and Bulgarian is not shown instead. */
export function note(note_bg: string | null, locale: Locale): string | null {
  return locale === 'bg' ? note_bg : null
}

export function ungroupedLabel(universe: CatalogueUniverse, locale: Locale): string | null {
  return locale === 'bg' ? universe.ungrouped_label_bg : universe.ungrouped_label_en
}

const EDITION_KEYS: Record<string, MessageKey> = {
  'paper bg': 'edition.paper.bg',
  'ebook bg': 'edition.ebook.bg',
  'audio bg': 'edition.audio.bg',
  'paper en': 'edition.paper.en',
  'ebook en': 'edition.ebook.en',
  'audio en': 'edition.audio.en',
}

export function editionKey(medium: string | null, language: string): MessageKey {
  return EDITION_KEYS[`${medium ?? ''} ${language}`] ?? 'edition.other'
}

/** Works in no sub-series, in publication order. */
export function outsideSeries(catalogue: Loaded): CatalogueWork[] {
  return catalogue.works.filter((work) => work.series.length === 0)
}

export function workBySlug(catalogue: Loaded, slug: string | undefined): CatalogueWork | undefined {
  return catalogue.works.find((work) => work.slug === slug)
}

/** Fill `{name}` placeholders in a translated or stored string. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  )
}
